//***************
//CodeValuesDB
//***************

import moment from 'moment';
import {CodeListValue} from "../../../components/FormIO/AutoComplete/lib/business-objects/CodeListValue";
import { getTableValuesByGroupName } from '../../Utils/IndexedDBUtils';

export const CodeValueStore = 'CodeValueStore';

export interface ICodeValuesEntry {
	created: string;
	groupName: string;
	dataArray: IDataRecord[];
}

export interface IDataRecord {
	name;
	description;
	active;
	version;
	modifiedBy;
	modifiedDate;
	id;
}

export default class CodeValuesDB {
	static CodeValuesDB: IDBDatabase;
	private static groupValueCache: { [groupName: string]: any[] } = {};
	private static groupValueRequests: { [groupName: string]: Promise<any[]> } = {};
	private static sortedGroupValueCache: { [cacheKey: string]: any[] } = {};

	static initializeDB = (db: IDBDatabase) => {
		CodeValuesDB.CodeValuesDB = db;
	};

	public static get codeValueCache(): any[] {
		if (!(window as any).codeValueCache) {
			(window as any).codeValueCache = [];
		}
		return (window as any).codeValueCache;
	}
	public static set codeValueCache(value: any[]) {
		(window as any).codeValueCache = value;
	}

	static createStore = (db: IDBDatabase) => {
		let ieStore = db.createObjectStore(CodeValueStore, {
			keyPath: 'groupName'
		});
		ieStore.createIndex('ieCreatedIndex', [ 'created' ]);
		ieStore.createIndex('ieGroupName', [ 'groupName' ]);
		ieStore.transaction.oncomplete = (event: any) => {
			console.log('codeValues entry collection created!');
		};
	};

	private static wrapcodeValuesRequest<T>(request: IDBRequest): Promise<T> {
		return new Promise((resolve, reject) => {
			request.onsuccess = () => {
				resolve(request.result);
			};
			request.onerror = (err) => reject(err);
		});
	}

	static AddCodeValuesEntriesToDB = async (entries: any): Promise<string> => {
		return new Promise((resolve, reject) => {
			Object.keys(entries).forEach(function(key) {
				const codeValuesEntry: ICodeValuesEntry = {
					groupName: key,
					created: moment().format('YYYY/MM/DD'),
					dataArray: entries[key]
				};
				CodeValuesDB.groupValueCache[key] = codeValuesEntry.dataArray;
				delete CodeValuesDB.groupValueRequests[key];
				delete CodeValuesDB.sortedGroupValueCache[key + ':name'];
				delete CodeValuesDB.sortedGroupValueCache[key + ':description'];

				try {
					CodeValuesDB.CodeValuesDB
						.transaction(CodeValueStore, 'readwrite')
						.objectStore(CodeValueStore)
						.put(codeValuesEntry);
				} catch (ex) {
					console.log('Error Adding codeValues entry', ex);
					reject('Error Adding codeValues entry');
				}
			});
			console.log('Finished Adding codeValues Entries');
			resolve('Finished Adding codeValues Entries');
		});
	};

	/**
	 * @description get code value list by code list valueid
	 * @param {number} codeListId - code id for values
	 * @param existingValue
	 * @returns database transaction

	static GetCodeValueListByCodeId = (codeListId: number, existingValue?: string | string[]): CodeListValue[] => {
		existingValue = existingValue ? existingValue : [];
		existingValue = Array.isArray(existingValue) ? existingValue : [ existingValue ];

		return CodeValuesDB.codeValueCache.filter(
			(codeValue: CodeListValue) =>
				codeValue.codeListId == codeListId && (!codeValue.disabled || existingValue.includes(codeValue.value))

		);
	};
	 */
	static GetCodeValueListByCodeId = (codeListId: number, existingValue?: string | string[]): CodeListValue[] => {
		existingValue = existingValue ? existingValue : [];
		existingValue = Array.isArray(existingValue) ? existingValue : [ existingValue ];

		return CodeValuesDB.codeValueCache.filter(
			(codeValue: CodeListValue) =>
				codeValue.codeListId == codeListId && (!codeValue.disabled || existingValue.includes(codeValue.value))

		);
	};


	/**
	 * @description get code value list by parent id
	 * @param {number} codeListId - list id
	 * @param {number} parentCodeListValueId - parent id for values
	 * @param existingValue
	 * @returns database transaction
	 */
	static GetCodeValueListByParentId = (
		codeListId: number,
		parentCodeListValueId: number,
		existingValue?: string | string[]
	): CodeListValue[] => {
		existingValue = existingValue ? existingValue : [];
		existingValue = Array.isArray(existingValue) ? existingValue : [existingValue];

		return CodeValuesDB.codeValueCache.filter(
			codeValue =>
				codeValue.codeListId == codeListId &&
				parentCodeListValueId == codeValue.parentCodeListValueId &&
				(!codeValue.disabled || existingValue.includes(codeValue.value))
		);
	};

	static GetCodeValueListByGroupname = async (groupName?: string, sortByDescription:boolean = false): Promise<any[]> => {
		if (typeof groupName !== 'string' || !groupName.trim()) {
			return [];
		}

		const sortCacheKey = groupName + (sortByDescription ? ':description' : ':name');
		if (CodeValuesDB.sortedGroupValueCache[sortCacheKey]) {
			return CodeValuesDB.sortedGroupValueCache[sortCacheKey];
		}

		let dataArray = CodeValuesDB.groupValueCache[groupName];
		if (!dataArray) {
			if (!CodeValuesDB.groupValueRequests[groupName]) {
				CodeValuesDB.groupValueRequests[groupName] = CodeValuesDB.wrapcodeValuesRequest<ICodeValuesEntry>(CodeValuesDB.CodeValuesDB
					.transaction(CodeValueStore, 'readonly')
					.objectStore(CodeValueStore)
					.index('ieGroupName')
					.get([groupName]))
					.then((codeValueRequest) => codeValueRequest && codeValueRequest.dataArray
						? codeValueRequest.dataArray
						: [])
					.then((result) => {
						delete CodeValuesDB.groupValueRequests[groupName];
						return result;
					}, (error) => {
						delete CodeValuesDB.groupValueRequests[groupName];
						throw error;
					});
			}

			dataArray = await CodeValuesDB.groupValueRequests[groupName];
			if (!CodeValuesDB.groupValueCache[groupName]) {
				CodeValuesDB.groupValueCache[groupName] = dataArray;
			} else {
				dataArray = CodeValuesDB.groupValueCache[groupName];
			}
		}

		if (CodeValuesDB.sortedGroupValueCache[sortCacheKey]) {
			return CodeValuesDB.sortedGroupValueCache[sortCacheKey];
		}

		let returnVal = dataArray.slice().sort((first,second) => {
			if (!sortByDescription)
				return (first.name < second.name) ? -1 : (first.name > second.name) ? 1 : 0;
			else {
				return (first.description < second.description) ? -1 : (first.description > second.description) ? 1 : 0;
			}
		});
		CodeValuesDB.sortedGroupValueCache[sortCacheKey] = returnVal;

		return returnVal;
	}
}
